<template>
    <div>
        <v-dialog
            :value="idx == modals.length - 1"
            :persistent="modal.persistent"
            @input="onDialogInput($event, modal, idx)"
            :scrollable="isDialogScrollable(modal)"
            :max-width="getMaxWidth(modal)"
            :content-class="getContentClass(modal)"
            v-for="(modal, idx) in modals"
            :width="getWidth(modal)"
            :key="modal.id"
        >
            <v-card
                class="pa-0 d-flex flex-column modal-dialog-card"
                :class="{
                    'modal-dialog-card--fill': modalUsesFillHeight(modal),
                    'modal-dialog-card--fixed': modalUsesFixedLayout(modal),
                }"
            >
                <component
                    class="w-100 d-flex flex-column flex-grow-1 min-height-0 overflow-hidden"
                    :is="modal.component"
                    :params="modal.params"
                >
                </component>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { max } from "vue-moment";
import { mapState, mapActions } from "vuex";

export default {
    props: {
        maxWidth: {
            type: [Number, String],
            default: "90%",
        },
        maxHeight: {
            type: [Number, String],
            default: "90%",
        },
        width: {
            type: [Number, String],
            default: 890,
        },
        height: {
            type: [Number, String],
            default: "90%",
        },
        persistent: {
            type: Boolean,
            default: true,
        },
    },
    computed: Object.assign(
        mapState("mcl", {
            currentModal: "modal",
            modals: "modals",
        }),
        {}
    ),
    methods: Object.assign(mapActions("mcl", ["hideModal"]), {
        onDialogInput(open, modal, idx) {
            if (!open && !modal.persistent && idx === this.modals.length - 1) {
                this.hideModal({ result: false });
            }
        },
        clickedOutside() {
            this.hideModal();
        },
        getMaxWidth(modal) {
            return modal?.component?.maxWidth || this.maxWidth;
        },
        getMaxHeight(modal) {
            return modal?.component?.maxHeight || this.maxHeight;
        },
        getWidth(modal) {
            return modal?.component?.width || this.width;
        },
        getContentClass(modal) {
            if (this.modalUsesFillHeight(modal)) {
                return "modal-dialog-content modal-dialog-content--fill";
            }
            if (this.modalUsesFixedLayout(modal)) {
                return "modal-dialog-content modal-dialog-content--fixed";
            }
            return "modal-dialog-content";
        },
        getHeight(modal) {
            return modal?.component?.height || this.height;
        },
        modalUsesFillHeight(modal) {
            return Boolean(modal?.component?.fillHeight);
        },
        /** Fixed-height modals (e.g. enterprise split) manage scroll inside the body; footer must stay pinned. */
        modalUsesFixedLayout(modal) {
            return modal?.component?.scrollable === false;
        },
        /** Scroll is handled inside ModalContent; Vuetify scrollable stretches the card to max height. */
        isDialogScrollable() {
            return false;
        },
    }),
};
</script>

<!-- Unscoped: content-class is applied on Vuetify's teleported v-dialog__content. -->
<style lang="scss">
.modal-dialog-content {
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-height: 0;
}

.modal-dialog-content--fixed {
    max-height: 90vh;
    min-height: 0;
    overflow: hidden;
}

.modal-dialog-content--fill {
    max-height: 90vh;
    height: 90vh;
    overflow: hidden;
}

.modal-dialog-content:not(.modal-dialog-content--fill):not(.modal-dialog-content--fixed)
    > .modal-dialog-card {
    flex: 0 1 auto;
    min-height: 0;
    max-height: 90vh;
}

/* On phones the 24px dialog margin + 90% max-width leave ~40px of unusable
   gutter, which the nested paddings then compound. Go edge to edge. */
@media only screen and (max-width: 599.98px) {
    .v-dialog.modal-dialog-content {
        margin: 0;
        width: 100% !important;
        max-width: 100% !important;
        max-height: 100%;
        border-radius: 0;
    }

    .modal-dialog-content--fill {
        height: 100%;
        max-height: 100%;
    }

    .modal-dialog-content--fixed {
        max-height: 100%;
    }

    .modal-dialog-content:not(.modal-dialog-content--fill):not(.modal-dialog-content--fixed)
        > .modal-dialog-card {
        max-height: 100%;
    }
}
</style>

<style scoped>
.modal-dialog-card {
    display: flex;
    flex-direction: column;
    flex: 0 1 auto;
    width: 100%;
    max-height: 90vh;
    min-height: 0;
    overflow: hidden;
}

.modal-dialog-card--fixed {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    max-height: 100%;
    overflow: hidden;
}

.modal-dialog-card--fill {
    max-height: 100%;
    height: 100%;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
}

@media only screen and (max-width: 599.98px) {
    .modal-dialog-card {
        max-height: 100%;
        border-radius: 0;
    }
}
</style>
